<template>
  <div
    class="ma-4 ma-md-0 mt-md-2 mb-md-10 buttons"
    :class="{shown: cursorJustMoved}"
    data-html2canvas-ignore
  >
    <v-btn
      :class="{hidden: showMoreButtons}"
      icon="add"
      :variant="$vuetify.display.mdAndUp ? 'flat' : 'elevated'"
      color="primary"
      v-tooltip:left="'View More Options'"
      @click="showMoreButtons = true"
      size="56"
    />
    <v-btn
      v-if="$route.name.startsWith('calendar') && $vuetify.display.mdAndUp"
      :class="{hidden: !showMoreButtons}"
      :style="
        $vuetify.display.mdAndUp ?
          {right: 'calc(4px - 72px * 3)'} :
          {bottom: 'calc(72px * 3)'}
      "
      icon="stylus_note"
      fab
      :loading="loadingAnnotation"
      color="primary"
      size="56"
      v-tooltip:left="'Add Handwritten Note'"
      :to="$routeTo(ROUTES.CALENDAR_ANNOTATIONS_ADD)"
    />
    <v-btn
      :class="{hidden: !showMoreButtons}"
      :style="
        $vuetify.display.mdAndUp ?
          {right: 'calc(4px - 72px * 2)'} :
          {bottom: 'calc(72px * 2)'}
      "
      icon
      fab
      color="primary"
      size="56"
      v-tooltip:left="'Add Voice Note'"
      @click="$emit('audio')"
    >
      <v-icon outlined>
        mic
      </v-icon>
    </v-btn>
    <v-btn
      :class="{hidden: !showMoreButtons}"
      :style="
        $vuetify.display.mdAndUp ?
          {right: 'calc(4px - 72px * 1)'} :
          {bottom: 'calc(72px * 1)'}
      "
      icon
      fab
      color="primary"
      size="56"
      v-tooltip:left="'Add Event'"
      :to="toAddEvent"
    >
      <v-icon outlined>
        calendar_add_on
      </v-icon>
    </v-btn>
    <v-btn
      :class="{hidden: !showMoreButtons}"
      :style="
        $vuetify.display.mdAndUp ?
          {right: 'calc(4px - 72px * 0)'} :
          {bottom: 'calc(72px * 0)'}
      "
      icon="close"
      fab
      color="primary"
      v-tooltip:left="'Close Menu'"
      @click="showMoreButtons = false"
      size="56"
    />
  </div>
</template>
<script lang="ts">
import {mapWritableState} from "pinia"
import {useAppStore} from "@/stores/app"
import {useCalendarStore} from "@/stores/calendar"
import {CalendarViews} from "@/enums/calendarViews"
import moment from "@/utils/moment"

export default {
  name: 'CalendarPageButtons',
  data() {
    return {
      showMoreButtons: false,
      cursorJustMoved: false,
      cursorTimer: null as any,
      loadingAnnotation: false
    }
  },
  emits: ['audio'],
  computed: {
    ...mapWritableState(useAppStore, ['pageScreenshot']),
    ...mapWritableState(useCalendarStore, ['calendarView', 'calendarStart']),

    isCurrentWeek() {
      return moment().isSame(moment(this.calendarStart), 'week')
    },

    isCurrentMonth() {
      return moment().isSame(moment(this.calendarStart), 'month')
    },

    isCurrentYear() {
      return moment().isSame(moment(this.calendarStart), 'year')
    },

    /**
     * Depending on the calendar view, we need to set the default date for the event creation
     */
    toAddEvent(): RouteLocationAsRelativeGeneric {
      let defaultDate
      switch (this.calendarView) {
        // For day view, use the day in view
        case CalendarViews.DAY:
          defaultDate = moment(this.calendarStart)
          break
        // For week view: if current week use today, otherwise use first day of viewed week
        case CalendarViews.WEEK:
          defaultDate = this.isCurrentWeek ? moment() : moment(this.calendarStart).startOf('week')
          break
        // For month view: if current month use today, otherwise use first day of viewed month
        case CalendarViews.MONTH:
          defaultDate = this.isCurrentMonth ? moment() : moment(this.calendarStart).startOf('month')
          break
        // For year view: if current year use today, otherwise use January 1st of viewed year
        case CalendarViews.YEAR:
          defaultDate = this.isCurrentYear ? moment() : moment(this.calendarStart).startOf('year')
          break
      }

      // Get next hour for now and set the time to next hour
      const nextHour = moment().startOf('hour').add(1, 'hour')
      defaultDate.hour(nextHour.hour()).minute(0)

      const start = defaultDate.format()
      const end   = defaultDate.clone().add(1, 'hour').format()
      const route = this.$routeTo(this.ROUTES.EVENT_ADD, {}, { start, end })
      return route
    }
  },
  mounted() {
    document.addEventListener('pointermove', this.handleMove)
  },
  beforeUnmount() {
    document.removeEventListener('pointermove', this.handleMove)
  },
  methods: {
    handleMove() {
      if (this.cursorTimer) {
        clearTimeout(this.cursorTimer)
      }
      this.cursorJustMoved = true
      this.cursorTimer = setTimeout(() => {
        this.cursorJustMoved = false
      }, 1000)
    },
  }
}
</script>
<style lang="scss" scoped>
.buttons {
  position: fixed;
  width: 4rem;
  z-index: 55;
  bottom: var(--v-safe-area-bottom);
  right: 0;
  height: 3.5rem;

  opacity: 1;

  @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
    position: relative;
  }

  &.shown, &:hover {
    opacity: 1;
  }

  .v-btn {
    opacity: 1;
    position: absolute;
    right: 0;
    z-index: 51;

    transition: all .3s;

    @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
      right: 0.25rem;
    }

    &.hidden {
      bottom: 0 !important;
      opacity: 0;
      z-index: 0;
      @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
        right: 0.25rem !important;
      }
    }
  }
}
</style>
